import { FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline'
import {
  MainPage,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
} from '../../components'
import { inputClassName } from '../../components/FormFields/FormText'
import { printHtmlContent, printIframeContent } from '../../utils/print'
import { alertService } from '../../services/alertService'
import {
  SHIPMENT_LABEL_FORMATS,
  ShipmentLabelFormat,
  parseShipmentLabelFormat,
  useShipmentLabel,
} from '../../hooks/useShipmentLabel'

/**
 * Nombres aceptados para el parámetro de guías. `guides` es el canónico;
 * `shipments` se acepta porque es como se llama el campo del API
 * (`shipment_identifications`).
 */
const GUIDES_PARAMS = ['guides', 'shipments']

const DEFAULT_FORMAT: ShipmentLabelFormat = 'pdf'

/** Separa por coma (y también por `;` o salto de línea, si se pega un listado). */
const splitGuides = (raw: string): string[] =>
  raw
    .split(/[,;\n\r]+/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0)

/**
 * Vista de etiqueta dirigida por URL:
 *
 *   /shipments/label?guides=1234,5678&format=pdf
 *
 * Lee las guías (separadas por coma) y el formato (`html`, `pdf`, `pdf-letter`),
 * llama a `POST /label` del backend Django y muestra el resultado en pantalla:
 * el HTML embebido o el PDF en un visor, más el `code`/`message` que respondió
 * el servicio.
 */
const ShipmentLabelView: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const rawGuides = useMemo(() => {
    const found = GUIDES_PARAMS.map((name) => searchParams.get(name)).find(
      (value) => value !== null
    )
    return found ?? ''
  }, [searchParams])

  const rawFormat = searchParams.get('format')

  const guides = useMemo(() => splitGuides(rawGuides), [rawGuides])

  // `format` ausente vale como `pdf`; presente pero inválido es un error que se
  // muestra en pantalla, no un fallback silencioso.
  const format = rawFormat === null ? DEFAULT_FORMAT : parseShipmentLabelFormat(rawFormat)

  const paramError = !guides.length
    ? `Falta el parámetro "guides" con el número de guía (varios separados por coma).`
    : format === null
    ? `Formato "${rawFormat}" no válido. Use uno de: ${SHIPMENT_LABEL_FORMATS.join(
        ', '
      )}.`
    : null

  const { loading, error, htmlContent, pdfUrl, message, code, labelFormat, reload } =
    useShipmentLabel(guides, format ?? DEFAULT_FORMAT, !paramError)

  // Campos del formulario: espejo editable de la URL, que es la fuente de verdad.
  const [guidesInput, setGuidesInput] = useState(rawGuides)
  const [formatInput, setFormatInput] = useState<ShipmentLabelFormat>(
    format ?? DEFAULT_FORMAT
  )

  useEffect(() => {
    setGuidesInput(rawGuides)
  }, [rawGuides])

  useEffect(() => {
    if (format) setFormatInput(format)
  }, [format])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setSearchParams({
      guides: splitGuides(guidesInput).join(','),
      format: formatInput,
    })
  }

  /**
   * HTML: se imprime el contenido en un iframe propio, así el diálogo solo
   * recibe la etiqueta y no el layout de la app.
   * PDF: se imprime el visor que ya está en pantalla.
   */
  const handlePrint = () => {
    if (htmlContent) {
      printHtmlContent(htmlContent, {
        title: `Etiqueta ${guides.join(', ')}`,
      }).catch((error: any) =>
        alertService.error(
          'No se pudo imprimir la etiqueta.',
          error?.message ?? ''
        )
      )
      return
    }

    if (!printIframeContent(iframeRef.current)) {
      alertService.error('No se pudo imprimir la etiqueta.')
    }
  }

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer')
      return
    }
    if (htmlContent) {
      const url = window.URL.createObjectURL(
        new Blob([htmlContent], { type: 'text/html' })
      )
      window.open(url, '_blank', 'noopener,noreferrer')
      // No revocamos de inmediato: la pestaña nueva todavía lo está cargando.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }
  }

  const hasContent = !!htmlContent || !!pdfUrl
  const displayedError = paramError ?? error

  return (
    <MainPage>
      <div className="flex flex-1 flex-col h-full">
        <PageTitle title="Etiqueta de guía" back />

        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4"
        >
          <div className="flex flex-1 flex-col">
            <label
              htmlFor="label-guides"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Guías (separadas por coma)
            </label>
            <input
              id="label-guides"
              type="text"
              value={guidesInput}
              onChange={(event) => setGuidesInput(event.target.value)}
              placeholder="Ej: 12345678, 12345679"
              className={inputClassName}
            />
          </div>

          <div className="flex flex-col sm:w-52">
            <label
              htmlFor="label-format"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Formato
            </label>
            <select
              id="label-format"
              value={formatInput}
              onChange={(event) =>
                setFormatInput(event.target.value as ShipmentLabelFormat)
              }
              className={inputClassName}
            >
              {SHIPMENT_LABEL_FORMATS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <PrimaryButton type="submit" disabled={!guidesInput.trim()}>
              <span className="w-20">Ver etiqueta</span>
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={reload}
              disabled={!!paramError || loading}
              title="Volver a pedir la etiqueta al servicio"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </SecondaryButton>
          </div>
        </form>

        {/* Resultado del servicio: siempre visible, haya salido bien o mal. */}
        <div
          className={classNames(
            'rounded-md border px-4 py-3 text-sm mb-4',
            displayedError
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-gray-300 bg-gray-50 text-gray-700'
          )}
        >
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>
              <b>Guías ({guides.length}):</b>{' '}
              {guides.length ? guides.join(', ') : '—'}
            </span>
            <span>
              <b>Formato solicitado:</b> {format ?? rawFormat ?? '—'}
            </span>
            <span>
              <b>Formato devuelto:</b> {labelFormat ?? '—'}
            </span>
            {!!code && (
              <span>
                <b>Código:</b> {code}
              </span>
            )}
          </div>
          {(displayedError || message) && (
            <div className="mt-2">
              <b>{displayedError ? 'Error:' : 'Mensaje:'}</b>{' '}
              {displayedError ?? message}
            </div>
          )}
        </div>

        <div className="flex flex-1 items-center justify-center border border-gray-300 rounded-md overflow-hidden bg-white min-h-[32rem]">
          {loading && <p className="text-lg text-gray-600">Generando etiqueta...</p>}

          {!loading && !!displayedError && (
            <p className="text-lg text-gray-500 px-6 text-center">
              No hay etiqueta para mostrar.
            </p>
          )}

          {!loading && !displayedError && htmlContent && (
            <iframe
              ref={iframeRef}
              title="Etiqueta"
              srcDoc={htmlContent}
              width="100%"
              height="100%"
              className="min-h-[32rem]"
            />
          )}

          {!loading && !displayedError && pdfUrl && (
            <iframe
              ref={iframeRef}
              title="Etiqueta"
              src={pdfUrl}
              width="100%"
              height="100%"
              className="min-h-[32rem]"
            />
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <SecondaryButton
            onClick={handleOpenInNewTab}
            disabled={loading || !hasContent}
          >
            <ArrowTopRightOnSquareIcon className="w-5 h-5 mr-2" />
            <span>Abrir en pestaña</span>
          </SecondaryButton>
          <PrimaryButton onClick={handlePrint} disabled={loading || !hasContent}>
            <PrinterIcon className="w-5 h-5 mr-2" />
            <span>Imprimir</span>
          </PrimaryButton>
        </div>
      </div>
    </MainPage>
  )
}

export default ShipmentLabelView
